import type { StatAllocation } from "@/game/model/character-stats";
import { STAT_IDS } from "@/game/model/character-stats";

const STORAGE_PREFIX = "mind-survival/equipment/v1";

export type EquipmentSlotId =
  | "helmet"
  | "decoration"
  | "weapon"
  | "body"
  | "gloves"
  | "boots"
  | "cape"
  | "elixir";

export const EQUIPMENT_SLOT_ORDER: EquipmentSlotId[] = [
  "helmet",
  "decoration",
  "weapon",
  "body",
  "gloves",
  "boots",
  "cape",
  "elixir",
];

export const EQUIPMENT_SLOT_LABEL_TH: Record<EquipmentSlotId, string> = {
  helmet: "หมวก",
  decoration: "เครื่องประดับ",
  weapon: "อาวุธ",
  body: "ชุด",
  gloves: "ถุงมือ",
  boots: "รองเท้า",
  cape: "ผ้าคลุม",
  elixir: "ยา",
};

export type EquipmentItemDef = {
  id: string;
  slot: EquipmentSlotId;
  nameTh: string;
  icon: string;
  bonus: Partial<StatAllocation>;
};

export const EQUIPMENT_ITEMS: Record<string, EquipmentItemDef> = {
  "starter-hood": {
    id: "starter-hood",
    slot: "helmet",
    nameTh: "หมวกผ้า",
    icon: "🧢",
    bonus: { defense: 1 },
  },
  "starter-staff": {
    id: "starter-staff",
    slot: "weapon",
    nameTh: "ไม้เท้าไม้",
    icon: "🪄",
    bonus: { attack: 2 },
  },
  "starter-vest": {
    id: "starter-vest",
    slot: "body",
    nameTh: "เสื้อผ้า",
    icon: "🥋",
    bonus: { defense: 2 },
  },
  "starter-gloves": {
    id: "starter-gloves",
    slot: "gloves",
    nameTh: "ถุงมือ",
    icon: "🧤",
    bonus: { critRate: 1 },
  },
  "starter-boots": {
    id: "starter-boots",
    slot: "boots",
    nameTh: "รองเท้า",
    icon: "👢",
    bonus: { moveSpeed: 2 },
  },
  "starter-charm": {
    id: "starter-charm",
    slot: "decoration",
    nameTh: "จี้โชค",
    icon: "📿",
    bonus: { critDamage: 1 },
  },
};

const DEFAULT_INVENTORY = Object.keys(EQUIPMENT_ITEMS);

export type EquipmentState = {
  version: 1;
  equipped: Partial<Record<EquipmentSlotId, string>>;
  inventory: string[];
};

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function createDefaultEquipmentState(): EquipmentState {
  return {
    version: 1,
    equipped: {},
    inventory: [...DEFAULT_INVENTORY],
  };
}

export function loadEquipmentState(userId: string): EquipmentState {
  const raw = localStorage.getItem(storageKey(userId));
  if (!raw) return createDefaultEquipmentState();
  try {
    const parsed = JSON.parse(raw) as EquipmentState;
    if (parsed.version !== 1 || !Array.isArray(parsed.inventory)) {
      return createDefaultEquipmentState();
    }
    return {
      version: 1,
      equipped: parsed.equipped ?? {},
      inventory: parsed.inventory.filter((id) => EQUIPMENT_ITEMS[id]),
    };
  } catch {
    localStorage.removeItem(storageKey(userId));
    return createDefaultEquipmentState();
  }
}

export function saveEquipmentState(userId: string, state: EquipmentState): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(state));
  import("@/cloud/player-cloud-scheduler").then((m) =>
    m.schedulePlayerCloudPush(userId, "gameState"),
  );
}

export function equipmentStatBonus(
  state: EquipmentState,
): Partial<StatAllocation> | undefined {
  const out: Partial<StatAllocation> = {};
  for (const slot of EQUIPMENT_SLOT_ORDER) {
    const itemId = state.equipped[slot];
    if (!itemId) continue;
    const item = EQUIPMENT_ITEMS[itemId];
    if (!item) continue;
    for (const id of STAT_IDS) {
      const extra = item.bonus[id];
      if (typeof extra === "number" && extra > 0) {
        out[id] = (out[id] ?? 0) + Math.floor(extra);
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function inventoryForSlot(
  state: EquipmentState,
  slot: EquipmentSlotId,
): EquipmentItemDef[] {
  const ids = new Set<string>();
  for (const id of state.inventory) {
    const item = EQUIPMENT_ITEMS[id];
    if (item?.slot === slot) ids.add(id);
  }
  for (const id of Object.values(state.equipped)) {
    if (!id) continue;
    const item = EQUIPMENT_ITEMS[id];
    if (item?.slot === slot) ids.add(id);
  }
  return [...ids].map((id) => EQUIPMENT_ITEMS[id]).filter(Boolean);
}

export function equipItem(
  state: EquipmentState,
  itemId: string,
): EquipmentState {
  const item = EQUIPMENT_ITEMS[itemId];
  if (!item) return state;
  if (!state.inventory.includes(itemId) && state.equipped[item.slot] !== itemId) {
    return state;
  }
  const nextEquipped = { ...state.equipped, [item.slot]: itemId };
  return { ...state, equipped: nextEquipped };
}

export function unequipSlot(
  state: EquipmentState,
  slot: EquipmentSlotId,
): EquipmentState {
  if (!state.equipped[slot]) return state;
  const nextEquipped = { ...state.equipped };
  delete nextEquipped[slot];
  return { ...state, equipped: nextEquipped };
}
